// S3Utils.js - Utilities for Amazon S3 operations
import { PutObjectCommand, GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { getS3Client } from './awsClients';
import { CURRICULUM_MAP } from '../config';

// Default region and bucket name
const DEFAULT_REGION = process.env.REACT_APP_AWS_REGION || 'us-west-2';
const DEFAULT_BUCKET = process.env.REACT_APP_S3_BUCKET_IMAGES || 'ai-math-tutor-images';
const LESSON_BLOCKS_BUCKET = process.env.REACT_APP_S3_BUCKET_LESSON_BLOCKS || 'ai-math-tutor-lesson-blocks';
const EXAM_QUESTIONS_BUCKET = process.env.REACT_APP_S3_BUCKET_EXAM_QUESTIONS || 'ai-math-tutor-exam-questions';
// const USER_UPLOADS_BUCKET = process.env.REACT_APP_S3_BUCKET_UPLOADS || 'ai-math-tutor-user-uploads';

/**
 * Get a signed URL for an S3 object
 * @param {Object} options - Request options
 * @param {string} options.bucket - S3 bucket name
 * @param {string} options.key - Object key
 * @param {number} options.expiresIn - URL expiry in seconds
 * @returns {Promise<string>} - Signed URL
 */
export async function getSignedS3Url({ bucket = DEFAULT_BUCKET, key, expiresIn = 3600 }) {
  try {
    const s3Client = await getS3Client();
    const command = new GetObjectCommand({
      Bucket: bucket,
      Key: key,
    });

    return await getSignedUrl(s3Client, command, { expiresIn });
  } catch (error) {
    console.error('Error getting signed S3 URL:', error);
    throw error;
  }
}

/**
 * Upload a file to S3
 * @param {Object} options - Upload options
 * @param {string} options.bucket - S3 bucket name
 * @param {string} options.key - Object key
 * @param {Buffer|Blob|string} options.body - File content
 * @param {string} options.contentType - Content type
 * @param {boolean} options.makePublic - Whether to make the object public (using bucket policy, not ACLs)
 * @returns {Promise<Object>} - Upload result
 */
export async function uploadToS3({ 
  bucket = DEFAULT_BUCKET, 
  key, 
  body, 
  contentType = 'application/octet-stream',
  makePublic = false 
}) {
  try {
    const s3Client = await getS3Client();
    
    const params = {
      Bucket: bucket,
      Key: key,
      Body: body,
      ContentType: contentType,
      // Removed ACL parameter since bucket doesn't support ACLs
    };
    
    const command = new PutObjectCommand(params);
    const result = await s3Client.send(command);
    
    // Return the URL of the uploaded object
    // If the bucket policy allows public access, direct URL will work
    const objectUrl = makePublic 
      ? `https://${bucket}.s3.${DEFAULT_REGION}.amazonaws.com/${key}`
      : await getSignedS3Url({ bucket, key });
      
    return {
      success: true,
      url: objectUrl,
      key,
      bucket,
      etag: result.ETag
    };
  } catch (error) {
    console.error('Error uploading to S3:', error);
    throw error;
  }
}

/**
 * Upload a diagram image (from data URL) to S3.
 * @param {string} dataUrl - Data URL of the diagram image (e.g., "data:image/png;base64,...").
 * @param {string} diagramIdentifier - A string to help create a unique and descriptive filename (e.g., function expression, shape type, diagram title).
 * @param {string} diagramType - A general type for folder organization (e.g., 'graph', 'geometric_shape', 'venn_diagram').
 * @param {Object} context - Additional context, expected to contain sessionId and userId.
 * @returns {Promise<string|null>} - The URL of the uploaded diagram, or null on failure.
 */
export async function uploadDiagramToS3(dataUrl, diagramIdentifier, diagramType = 'diagram', context = {}) {
  if (!dataUrl || !diagramIdentifier) {
    console.error('uploadDiagramToS3: dataUrl and diagramIdentifier are required.');
    return null;
  }
  if (!context.sessionId || !context.userId) {
      console.warn('uploadDiagramToS3: sessionId or userId missing from context. S3 path might be less organized.');
      // Decide if you want to proceed or return null if context is essential for your pathing
  }

  try {
    const parts = dataUrl.split(',');
    if (parts.length < 2 || !parts[0].includes('base64')) {
        throw new Error('Invalid data URL format. Expected "data:[<mediatype>];base64,<data>"');
    }
    const base64Data = parts[1];
    const binaryData = Buffer.from(base64Data, 'base64');
    
    // Determine content type from data URL
    let contentType = 'image/png'; // Default
    const mimeMatch = parts[0].match(/data:(image\/[^;]+);base64/);
    if (mimeMatch && mimeMatch[1]) {
        contentType = mimeMatch[1];
    }
    const extension = contentType.split('/')[1] || 'png';


    // Create a safe filename from the identifier
    const safeIdentifier = diagramIdentifier.replace(/[^a-zA-Z0-9_.-]/g, '_').substring(0, 50); // Increased length slightly
    const timestamp = Date.now();
    
    // Use diagramType for better folder organization in S3
    // You could also include userId or sessionId in the path if desired for partitioning
    const key = `diagrams/${diagramType}/${safeIdentifier}_${timestamp}.${extension}`;
    
    // Upload to S3 (assuming your uploadToS3 helper handles the actual AWS SDK interaction)
    const result = await uploadToS3({ // This is your existing internal S3 upload helper
      key,
      body: binaryData,
      contentType: contentType, // Use detected or default content type
      makePublic: true // Or based on your application's needs
    });
    
    if (result && result.url) {
        console.log(`Diagram uploaded to S3: ${result.url}`);
        return result.url;
    } else {
        console.error('S3 upload result did not contain a URL:', result);
        return null;
    }
  } catch (error) {
    console.error('Failed to upload diagram to S3:', error);
    return null; // So the app can fall back to data URL if needed
  }
}

/**
 * Upload a file (image, PDF, etc.) to S3 for AI processing
 * @param {File} file - The file object from file input or drag & drop
 * @param {object} context - Contains sessionId and userId
 * @param {string} context.sessionId - The current session ID
 * @param {string} context.userId - The current user ID
 * @returns {Promise<string>} - The HTTPS URL of the uploaded file
 */
export async function uploadFileToS3(file, { sessionId, userId }) {
  if (!userId || !sessionId) {
    throw new Error('User ID and Session ID are required to upload files.');
  }
  
  try {
    // Read file as ArrayBuffer
    const fileBuffer = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(Buffer.from(reader.result));
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
    
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    
    // Save to user-uploads/${userId}/${sessionId}/${timestamp}_${filename}
    const key = `ai-math-tutor-user-uploads/${userId}/${sessionId}/${timestamp}_${safeFileName}`;
    
    // Upload to S3
    const result = await uploadToS3({
      bucket: DEFAULT_BUCKET,
      key,
      body: fileBuffer,
      contentType: file.type,
      makePublic: true // For ease of access in the UI
    });
    
    console.log(`File ${file.name} uploaded to S3:`, result.url);
    return result.url;
  } catch (error) {
    console.error('Failed to upload file to S3:', error);
    throw new Error(`Could not upload file: ${error.message}`);
  }
}

/**
 * Upload a user sketch image (from DrawPad) to S3.
 * Wraps the generic uploadToS3 function with specific path conventions.
 * @param {string|File} dataOrFile - Data URL of the sketch image or a File object
 * @param {object} context - Contains sessionId and userId.
 * @param {string} context.sessionId - The current session ID.
 * @param {string} context.userId - The current user ID.
 * @returns {Promise<string>} - The HTTPS URL of the uploaded image.
 */
export async function uploadImageToS3(dataOrFile, { sessionId, userId }) {
  if (!userId || !sessionId) {
    throw new Error('User ID and Session ID are required to upload sketch.');
  }
  
  try {
    let binaryData, contentType, filename;
    
    // Handle both data URLs (from canvas) and File objects
    if (typeof dataOrFile === 'string' && dataOrFile.startsWith('data:')) {
      // It's a data URL from canvas
      const matches = dataOrFile.match(/^data:(.+);base64,(.*)$/);
      if (!matches || matches.length !== 3) {
        throw new Error("Invalid data URL format");
      }
      
      contentType = matches[1];
      const base64Data = matches[2];
      binaryData = Buffer.from(base64Data, 'base64');
      filename = 'sketch.png';
    } else if (dataOrFile instanceof File) {
      // It's a File object from file input
      contentType = dataOrFile.type;
      filename = dataOrFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      
      // Read file as ArrayBuffer
      binaryData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(Buffer.from(reader.result));
        reader.onerror = reject;
        reader.readAsArrayBuffer(dataOrFile);
      });
    } else {
      throw new Error("Unsupported image data format");
    }
    
    const timestamp = Date.now();
    const key = `ai-math-tutor-user-uploads/${userId}/${sessionId}/${timestamp}_${filename}`;

    // Upload to S3
    const result = await uploadToS3({
      bucket: DEFAULT_BUCKET,
      key,
      body: binaryData,
      contentType,
      makePublic: true
    });

    console.log('Image uploaded to S3:', result.url);
    return result.url;
  } catch (error) {
    console.error('Failed to upload image to S3:', error);
    throw new Error('Could not upload image: ' + error.message);
  }
}

/**
 * Fetch lesson block JSON from S3
 * @param {string} moduleId - The ID of the module (e.g., year_11_standard_cambridge_1_1)
 * @param {Object} userProfile - User profile containing state, textbook, etc.
 * @returns {Promise<Object>} - The lesson block data as a parsed JSON object
 */
export async function fetchLessonBlockFromS3(moduleId, userProfile) {
  if (!moduleId) {
    throw new Error('Module ID is required to fetch lesson block.');
  }
  
  try {
    const s3Client = await getS3Client();
    
    // Extract state, textbook, and curriculum from user profile
    const state = userProfile?.state?.toLowerCase() || 'nsw';
    let textbook = userProfile?.textbook?.toLowerCase() || 'cambridge';
    const yearLevel = userProfile?.yearLevel || '11';
    const mathsType = userProfile?.mathsType?.toLowerCase() || 'standard';
    const yearMathCombination = userProfile?.yearMathCombination || '11_standard';
    const curriculumKey = `${yearLevel}_${mathsType}`;

    // If the user selected the generic textbook option, determine which
    // textbook to mirror by inspecting the curriculum map. This ensures the
    // mapping only needs to be defined in one place (curriculum.js).
    if (textbook === 'general') {
      const stateKey = state.toUpperCase();
      const modules = CURRICULUM_MAP[stateKey]?.general?.[curriculumKey] || [];
      const idSample = modules[0]?.id || '';
      if (idSample.includes('_cambridge_')) {
        textbook = 'cambridge';
      } else if (idSample.includes('_nelson_')) {
        textbook = 'nelson';
      }
    }
    
    // Construct the S3 key for the lesson block
    // Pattern: state/textbook/curriculum_key/moduleId.json
    const key = `${state}/${textbook}/${yearMathCombination}/${moduleId}.json`;
    
    console.log(`Fetching lesson block from S3: ${LESSON_BLOCKS_BUCKET}/${key}`);
    
    const command = new GetObjectCommand({
      Bucket: LESSON_BLOCKS_BUCKET,
      Key: key,
    });
    
    // Get the response from S3
    const response = await s3Client.send(command);
    
    // Convert the stream to text
    const reader = response.Body.getReader();
    const chunks = [];
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    
    const allBytes = new Uint8Array(chunks.reduce((totalLength, chunk) => totalLength + chunk.length, 0));
    let position = 0;
    
    for (const chunk of chunks) {
      allBytes.set(chunk, position);
      position += chunk.length;
    }
    
    const jsonText = new TextDecoder('utf-8').decode(allBytes);
    
    // Parse the JSON text to an object
    const lessonBlockData = JSON.parse(jsonText);
    
    return lessonBlockData;
  } catch (error) {
    console.error('Error fetching lesson block from S3:', error);
    // Return null so the app can fall back to default lesson block
    return null;
  }
}

/**
 * Fetch exam questions JSON from S3
 * @param {string} state - The state (e.g., 'NSW', 'VIC', 'QLD')
 * @param {string} yearLevelKey - The year level key (e.g., '11_standard', '12_advanced')
 * @returns {Promise<Array>} - The exam questions array
 */
export async function fetchExamQuestionsFromS3(state, yearLevelKey) {
  if (!state || !yearLevelKey) {
    throw new Error('State and year level key are required to fetch exam questions.');
  }
  
  try {
    const s3Client = await getS3Client();
    
    // Convert state to lowercase
    const stateKey = state.toLowerCase();
    
    // Construct the S3 key: state/year_level/state_year_level.json
    const key = `${stateKey}/${yearLevelKey}/${stateKey}_${yearLevelKey}.json`;
    
    console.log(`Debug: Input state="${state}", yearLevelKey="${yearLevelKey}"`);
    console.log(`Debug: Constructed S3 key="${key}"`);
    console.log(`Fetching exam questions from S3: ${EXAM_QUESTIONS_BUCKET}/${key}`);
    
    const command = new GetObjectCommand({
      Bucket: EXAM_QUESTIONS_BUCKET,
      Key: key,
    });
    
    // Get the response from S3
    const response = await s3Client.send(command);
    
    // Convert the stream to text
    const reader = response.Body.getReader();
    const chunks = [];
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    
    const allBytes = new Uint8Array(chunks.reduce((totalLength, chunk) => totalLength + chunk.length, 0));
    let position = 0;
    
    for (const chunk of chunks) {
      allBytes.set(chunk, position);
      position += chunk.length;
    }
    
    const jsonText = new TextDecoder('utf-8').decode(allBytes);
    
    // Parse the JSON text to an array
    const examQuestions = JSON.parse(jsonText);
    
    console.log(`Success: Fetched ${Array.isArray(examQuestions) ? examQuestions.length : 'unknown'} questions from S3`);
    
    return Array.isArray(examQuestions) ? examQuestions : [];
  } catch (error) {
    console.error('Error fetching exam questions from S3:', error);
    // Return empty array so the app can handle the empty state gracefully
    return [];
  }
}